import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { Course, CourseItem, CourseSeason } from "../types";
import { buildCourseEpisodeLink } from "../utils/course-link";
import { copyToClipboard, useSnackbar } from "../hooks/useSnackbar";

async function fetchCourses(): Promise<Course[]> {
  const response = await axios.get<Course[]>(ServerRoutes.courses);
  return response.data;
}

async function fetchSeasons(courseId: number): Promise<CourseSeason[]> {
  const response = await axios.get<CourseSeason[]>(
    `${ServerRoutes.courseSeasons}?courseId=${courseId}`,
  );
  return response.data;
}

async function fetchItems(courseId: number): Promise<CourseItem[]> {
  const response = await axios.get<CourseItem[]>(
    `${ServerRoutes.courseItems}?courseId=${courseId}`,
  );
  return response.data;
}

export function CourseLinkGeneratorSection() {
  const { notify, SnackbarHost } = useSnackbar();
  const [courseId, setCourseId] = useState<number | "">("");
  const [seasonId, setSeasonId] = useState<number | "">("");
  const [episodeId, setEpisodeId] = useState<number | "">("");
  const [referralCode, setReferralCode] = useState("");

  const coursesQuery = useQuery({
    queryKey: ["course-link-courses"],
    queryFn: fetchCourses,
  });
  const seasonsQuery = useQuery({
    queryKey: ["course-link-seasons", courseId],
    queryFn: () => fetchSeasons(Number(courseId)),
    enabled: courseId !== "",
  });
  const itemsQuery = useQuery({
    queryKey: ["course-link-items", courseId],
    queryFn: () => fetchItems(Number(courseId)),
    enabled: courseId !== "",
  });

  const seasons = useMemo(
    () => [...(seasonsQuery.data ?? [])].sort((a, b) => a.priority - b.priority),
    [seasonsQuery.data],
  );
  const episodes = useMemo(
    () =>
      [...(itemsQuery.data ?? [])]
        .filter((item) => item.season?.id === seasonId)
        .sort((a, b) => a.priority - b.priority),
    [itemsQuery.data, seasonId],
  );

  const generatedLink =
    courseId !== "" && seasonId !== "" && episodeId !== ""
      ? buildCourseEpisodeLink(
          courseId,
          seasonId,
          episodeId,
          referralCode || undefined,
        )
      : "";

  const handleCopy = async () => {
    const copied = await copyToClipboard(generatedLink);
    notify(
      copied ? "Episode link copied." : "Could not copy the link.",
      copied ? "success" : "error",
    );
  };

  if (coursesQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mx: "auto" }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Course Episode Link Generator
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Select a course, season and episode. The generated Telegram link opens
        that episode directly in the Mini App after authentication.
      </Typography>

      {coursesQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load courses.
        </Alert>
      ) : null}

      <Card sx={{ p: { xs: 2, sm: 3 }, display: "grid", gap: 2 }}>
        <FormControl fullWidth>
          <InputLabel id="link-course-label">Course</InputLabel>
          <Select
            labelId="link-course-label"
            label="Course"
            value={courseId}
            onChange={(event) => {
              setCourseId(Number(event.target.value));
              setSeasonId("");
              setEpisodeId("");
            }}
          >
            {(coursesQuery.data ?? []).map((course) => (
              <MenuItem key={course.id} value={course.id}>
                {course.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={courseId === "" || seasonsQuery.isLoading}>
          <InputLabel id="link-season-label">Season</InputLabel>
          <Select
            labelId="link-season-label"
            label="Season"
            value={seasonId}
            onChange={(event) => {
              setSeasonId(Number(event.target.value));
              setEpisodeId("");
            }}
          >
            {seasons.map((season) => (
              <MenuItem key={season.id} value={season.id}>
                {season.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          disabled={seasonId === "" || itemsQuery.isLoading}
        >
          <InputLabel id="link-episode-label">Episode</InputLabel>
          <Select
            labelId="link-episode-label"
            label="Episode"
            value={episodeId}
            onChange={(event) => setEpisodeId(Number(event.target.value))}
          >
            {episodes.map((episode) => (
              <MenuItem key={episode.id} value={episode.id}>
                {episode.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Referral code (optional)"
          value={referralCode}
          onChange={(event) => setReferralCode(event.target.value.trim())}
          helperText="Include a valid referral code if the link may be opened by new users."
          fullWidth
        />

        <TextField
          label="Generated link"
          value={generatedLink}
          placeholder="Select an episode to generate the link"
          multiline
          minRows={2}
          fullWidth
          InputProps={{ readOnly: true }}
        />

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            disabled={!generatedLink}
            onClick={handleCopy}
          >
            Copy link
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            disabled={!generatedLink}
            component="a"
            href={generatedLink || undefined}
            target="_blank"
            rel="noopener noreferrer"
          >
            Test link
          </Button>
        </Box>
      </Card>
      <SnackbarHost />
    </Box>
  );
}
